import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import axios, {AxiosResponse} from "axios";
import {RootState} from "../store";
import {toast} from "react-toastify";
import {Dayjs} from "dayjs";
import dayjs from "../../utils/dayjs";

const parseMovieData = (movies: MovieFromAPICall[]): Movie[] => {
    let parsedMovies: Movie[] = [];

    movies.map((movie: MovieFromAPICall) => {
        parsedMovies.push({
            id: Math.random(),
            title: movie.title,
            description: movie.opening_crawl,
            director: movie.director,
            producer: movie.producer,
            releaseDate: dayjs(movie.release_date).format("YYYY-MM-DD"),
            created: dayjs(movie.create).format("YYYY-MM-DD"),
            edited: dayjs(movie.edited).format("YYYY-MM-DD"),
            characters: movie.characters,
        });
        return movie;
    });

    return parsedMovies;
}

type MovieFromAPICall = {
    title: string;
    opening_crawl: string;
    director: string;
    producer: string;
    release_date: string;
    create: string;
    edited: string;
    characters: string[];
};

export const fetchMovies = createAsyncThunk(
    "post/fetchMovies", async (_, thunkAPI) => {
        try {
            await thunkAPI.dispatch(clearMovies());
            const response: AxiosResponse = await axios.get("https://swapi.dev/api/films");

            //const response: AxiosResponse = await axios.get("https://jsonplaceholder.typicode.com/posts");

            /*await Promise.all(response.data.results.map(async (data: { characters: string[]; }) => {
                let characters: string[] = [];
                await Promise.all(data.characters.map(async character => {
                    const charactersResponse: AxiosResponse = await axios.get(character);
                    characters.push(charactersResponse.data.name);
                }));
                data.characters = characters;
            }));*/

            return parseMovieData(response.data.results);
        } catch (error) {
            console.log(error)
            let errorMessage: string = "An unexpected error occurred";
            if (axios.isAxiosError(error)) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

export const fetchMovieById = createAsyncThunk(
    "post/fetchPostsByUserId", async (movieId: number, thunkAPI) => {
        try {
            await thunkAPI.dispatch(clearMovies());
            const response: AxiosResponse = await axios.get(`https://swapi.dev/api/films/${movieId}`);
            if (response.data.length > 0) {
                let movie: Movie[] = parseMovieData(response.data);
                return movie[0];
            }
            return {} as Movie;
        } catch (error) {
            let errorMessage: string = "An unexpected error occurred";
            if (axios.isAxiosError(error)) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

export type Movie = {
    id: number
    title: string
    description: string
    director: string
    producer: string
    releaseDate: string
    created: string
    edited: string
    characters: string[]
};

// Define a type for the slice state
export type MovieState = {
    movie: {
        data: {
            movies: Movie[]
            total: number
        }
        done: boolean
        error: string
    }
    movieById: {
        data: {
            movie: Movie
        }
        done: boolean
        error: string
    }
};

// Define the initial state using that type
const initialState: MovieState = {
    movie: {
        data: {
            movies: [],
            total: 0
        },
        done: false,
        error: ""
    },
    movieById: {
        data: {
            movie: {} as Movie
        },
        done: false,
        error: ""
    }
};

export const movieSlice = createSlice({
    name: "movie",
    initialState,
    reducers: {
        clearMovies: () => {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchMovies.fulfilled, (state, action) => {
            return {
                ...state,
                movie: {
                    ...state.movie,
                    data: {movies: action.payload, total: action.payload.length},
                    done: true,
                    error: ""
                }
            };
        });
        builder.addCase(fetchMovies.rejected, (state, action) => {
            return {...state, movie: {...state.movie, done: true, error: (action.payload as string)}};
        });
        builder.addCase(fetchMovies.pending, (state) => {
            return {...state, movie: {...state.movie, done: false, error: ""}};
        });
        builder.addCase(fetchMovieById.fulfilled, (state, action) => {
            return {
                ...state,
                movieById: {
                    ...state.movieById,
                    data: {movie: action.payload},
                    done: true,
                    error: ""
                }
            };
        });
        builder.addCase(fetchMovieById.rejected, (state, action) => {
            return {...state, movieById: {...state.movieById, done: true, error: (action.payload as string)}};
        });
        builder.addCase(fetchMovieById.pending, (state) => {
            return {...state, movieById: {...state.movieById, done: false, error: ""}};
        });
    },
});

export const {clearMovies} = movieSlice.actions;

export const getMovies = (state: RootState) => state.movie.movie;
export const getMovieById = (state: RootState) => state.movie.movieById;

/*
// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectCount = (state: RootState) => state.counter.value;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
export const incrementIfOdd =
    (amount: number): AppThunk =>
        (dispatch, getState) => {
            const currentValue = selectCount(getState());
            if (currentValue % 2 === 1) {
                dispatch(incrementByAmount(amount));
            }
        };*/

export default movieSlice.reducer;